import { trackEventWithAttribution } from "./posthog-helpers";

export const ACQUISITION_DEMO_EVENTS = {
  assigned: "acquisition_demo_assigned",
  completed: "acquisition_demo_video_completed",
  loaded: "acquisition_demo_loaded",
  progressed: "acquisition_demo_video_progressed",
  speakingStarted: "acquisition_demo_start_clicked",
  started: "acquisition_demo_video_started",
  viewed: "acquisition_demo_viewed",
} as const;

export type AcquisitionDemoEventName =
  (typeof ACQUISITION_DEMO_EVENTS)[keyof typeof ACQUISITION_DEMO_EVENTS];

export type AcquisitionDemoKind = "live_speaking" | "product_video";
export type AcquisitionDemoVideoProgress = 25 | 50 | 75;

export type AcquisitionDemoAnalyticsContext = {
  demoId: string;
  demoKind: AcquisitionDemoKind;
  pagePath: string;
  pageFamily: string;
  placement: string;
  experimentKey?: string;
  experimentVariant?: string;
  instructionLanguageCode?: string;
  languageLevel?: string;
  targetLanguageCode?: string;
  assignmentId?: string;
};

export type AcquisitionDemoEventProperties = {
  demo_id: string;
  demo_kind: AcquisitionDemoKind;
  page_path: string;
  page_family: string;
  placement: string;
  experiment_key?: string;
  experiment_variant?: string;
  instruction_language_code?: string;
  language_level?: string;
  target_language_code?: string;
  assignment_id?: string;
  video_progress_percent?: AcquisitionDemoVideoProgress;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

export function buildAcquisitionDemoEventProperties(
  context: AcquisitionDemoAnalyticsContext,
  properties: Pick<AcquisitionDemoEventProperties, "video_progress_percent"> = {}
): AcquisitionDemoEventProperties {
  return {
    demo_id: context.demoId,
    demo_kind: context.demoKind,
    page_path: context.pagePath,
    page_family: context.pageFamily,
    placement: context.placement,
    ...(context.experimentKey ? { experiment_key: context.experimentKey } : {}),
    ...(context.experimentVariant ? { experiment_variant: context.experimentVariant } : {}),
    ...(context.instructionLanguageCode
      ? { instruction_language_code: context.instructionLanguageCode }
      : {}),
    ...(context.languageLevel ? { language_level: context.languageLevel } : {}),
    ...(context.targetLanguageCode ? { target_language_code: context.targetLanguageCode } : {}),
    ...(context.assignmentId ? { assignment_id: context.assignmentId } : {}),
    ...properties,
  };
}

export function trackAcquisitionDemoEvent(
  posthog: PostHogLike | null | undefined,
  eventName: AcquisitionDemoEventName,
  context: AcquisitionDemoAnalyticsContext,
  properties: Pick<AcquisitionDemoEventProperties, "video_progress_percent"> = {}
) {
  const eventProperties = buildAcquisitionDemoEventProperties(context, properties);
  trackEventWithAttribution(posthog, eventName, eventProperties);
  return eventProperties;
}
